// A printed flier: the same copy as the email, laid out as a poster to hand out or hang up.
export interface FlyerContent {
  headline: string; subhead: string; body: string; offerLine: string; cta: string;
  products: { label: string; priceCents: number | null }[]; startsOn: string | null; endsOn: string | null;
}
export type FlyerSize = 'letter' | 'half' | 'quarter';
export const FLYER_SIZES: Record<FlyerSize, { name: string; w: number; h: number; k: number }> = {
  letter: { name: 'Full page, 8.5 × 11 in', w: 8.5, h: 11, k: 1.7 },
  half: { name: 'Half page, 5.5 × 8.5 in', w: 5.5, h: 8.5, k: 1.15 },
  quarter: { name: 'Quarter page, 4.25 × 5.5 in', w: 4.25, h: 5.5, k: 0.8 },
};
export const NOTICE = 'Must be 21+. Please drink responsibly.';

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const day = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });

export const FLYER_CSS = `
.fl{box-sizing:border-box;display:flex;flex-direction:column;background:#fff;color:#1b1b1b;font-family:Arial,Helvetica,sans-serif;font-size:calc(10pt*var(--k));line-height:1.3;text-align:center;overflow:hidden}
.fl *{box-sizing:border-box}
.fl .top{background:#14301f;color:#fff;padding:1.4em 1em 1.6em}
.fl .store{letter-spacing:.2em;color:#e1b04a;font-size:.8em;font-weight:700}
.fl .hl{font-size:2.7em;font-weight:900;line-height:1.05;margin:.35em 0 .2em;text-wrap:balance}
.fl .sub{font-size:1.15em;color:#dfe8e2}
.fl .main{flex:1;padding:1.1em 1.4em;display:flex;flex-direction:column;justify-content:center;gap:.8em}
.fl .body{font-size:1.2em}
.fl .offer{background:#fbf3dc;border:.15em dashed #e1b04a;border-radius:.4em;padding:.6em;font-size:1.6em;font-weight:900}
.fl .when{color:#555}
.fl table{width:100%;border-collapse:collapse;font-size:1.05em;text-align:left}
.fl td{padding:.35em 0;border-bottom:1px solid #ddd}.fl td.p{text-align:right;font-weight:800}
.fl .cta{font-size:1.5em;font-weight:900;color:#14301f}
.fl .addr{color:#555}
.fl .join{font-size:1em;color:#14301f;font-weight:700;border-top:1px solid #ddd;padding-top:.6em}
.fl .fine{background:#f4f4f4;color:#666;font-size:.7em;padding:.7em}
`;

export function flyerHtml(c: FlyerContent, store: { name: string; address: string | null }, size: FlyerSize, joinUrl: string | null): string {
  const s = FLYER_SIZES[size];
  const when = c.startsOn && c.endsOn ? `${day(c.startsOn)} to ${day(c.endsOn)}` : c.endsOn ? `Through ${day(c.endsOn)}` : c.startsOn ? `Starting ${day(c.startsOn)}` : '';
  const rows = c.products.map((p) => `<tr><td>${esc(p.label)}</td><td class="p">${p.priceCents != null ? money(p.priceCents) : ''}</td></tr>`).join('');
  return `<div class="fl" style="--k:${s.k};width:${s.w}in;height:${s.h}in">
    <div class="top"><div class="store">${esc(store.name.toUpperCase())}</div><div class="hl">${esc(c.headline)}</div>${c.subhead ? `<div class="sub">${esc(c.subhead)}</div>` : ''}</div>
    <div class="main">${c.body ? `<div class="body">${esc(c.body)}</div>` : ''}${c.offerLine ? `<div class="offer">${esc(c.offerLine)}</div>` : ''}${when ? `<div class="when">${esc(when)}</div>` : ''}
      ${rows ? `<table>${rows}</table>` : ''}
      <div class="cta">${esc(c.cta || `Visit ${store.name}`)}</div>${store.address ? `<div class="addr">${esc(store.address)}</div>` : ''}
      ${joinUrl ? `<div class="join">Get news and deals by email: ${esc(joinUrl)}</div>` : ''}</div>
    <div class="fine">${NOTICE}</div></div>`;
}

export function printFlyers(c: FlyerContent, store: { name: string; address: string | null }, size: FlyerSize, joinUrl: string | null, copies: number): boolean {
  const w = window.open('', '_blank');
  if (!w) return false;
  const s = FLYER_SIZES[size];
  const cards = Array.from({ length: copies }, () => `<div class="cell">${flyerHtml(c, store, size, joinUrl)}</div>`).join('');
  w.document.open();
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Flier</title><style>${FLYER_CSS}@page{margin:0} body{margin:0;display:flex;flex-wrap:wrap;align-content:flex-start}
    .cell{width:${s.w}in;height:${s.h}in;break-inside:avoid;page-break-inside:avoid}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body>${cards}</body></html>`);
  w.document.close(); w.focus();
  setTimeout(() => w.print(), 300);
  return true;
}
